// Building blocks of the forms built from jawdaplus-prototype-v4.html:
// consumer / merchant complaints, merchant code and sign-up, other requests.
import { Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import classNames from "classnames";
import { useSelector } from "react-redux";
import xss from "xss";
import Compressor from "compressorjs";
import sanitizeFilename from "sanitize-filename";
import { Camera, Check, Image as ImageIcon, ChevronDown, ChevronLeft, CircleEllipsis, CircleHelp, Download, List, Mic, Pause, Play, ScanBarcode, Search, X } from "lucide-react";
import styles from "../styles/components/wizard.module.scss";
import { dataLink } from "../config";
import { GUIDE_CODE_SVG, GUIDE_LOT_SVG } from "./guideFigures";
import LangButtons from "./LangButtons";
import IosInstallModal from "./IosInstallModal";
import AndroidInstallModal from "./AndroidInstallModal";
import Confetti from "./Confetti";
import { usePwaInstall } from "./hooks/usePwaInstall";


export { styles };

export const MAX_PHOTOS = 2;
export const MAX_TYPES = 2;

// ── Helpers ────────────────────────────────────────────────────────────────────

export const localized = (item, lang) =>
  (lang === "ar" && item.arabic_translation?.trim()) || (lang === "en" && item.english_translation?.trim()) || item.name;

// Free text (messages, comments, shop names): letters, digits, spaces and , . ! ? ( ) only (client
// request), within what the backend accepts. Backend rule (complaint comment):
//   /^[\p{Arabic}a-zA-Zé0-9ïâéèàçû@%äêëîôöùüÿ_.’,:?! \'\-°\^¨+=\(\)\n\t\r]+$/u
// It refuses capital accents (É), the Arabic comma and question mark (، ؟ are not "Arabic script"),
// tashkeel and tatweel, and unusual spaces: anything refused makes the whole complaint fail. So
// capital accents become lowercase, ، ؟ become , ?, unusual spaces become spaces, the rest goes.
const FREE_TEXT_ALLOWED = /[a-zA-Z0-9àâäçéèêëîïôöùûüÿ ,.!?()\n\r\t]/;
const ARABIC_SCRIPT = /\p{Script=Arabic}/u;
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;
export const cleanText = (value) =>
  Array.from(
    (value ?? "")
      .normalize("NFC") // "e" + accent mark (some phones) -> "é"
      .replace(/[ÀÂÄÇÉÈÊËÎÏÔÖÙÛÜŸ]/g, (letter) => letter.toLowerCase())
      .replace(/،/g, ",")
      .replace(/؟/g, "?")
      .replace(/[^\S\n\r\t]/g, " ")
  )
    .filter((ch) => FREE_TEXT_ALLOWED.test(ch) || (ARABIC_SCRIPT.test(ch) && LETTER_OR_DIGIT.test(ch)))
    .join("");

const normalize = (s) => (s ?? "").toString().normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().trim();

// Digits only; Arabic-Indic digits (٠١٢…) are converted
export const toDigits = (v) =>
  String(v)
    .replace(/[٠-٩]/g, (c) => c.charCodeAt(0) - 0x0660)
    .replace(/[۰-۹]/g, (c) => c.charCodeAt(0) - 0x06f0)
    .replace(/\D/g, "");

// "0555123456" -> "05 55 12 34 56", the format the previous forms sent
export const spacedPhone = (digits) => digits.match(/.{1,2}/g)?.join(" ") || "";

// "JJ/MM/AAAA" -> "AAAA-MM-JJ", or "" when incomplete, impossible, in the future or too old
export const parseDate = (text) => {
  const d = toDigits(text);
  if (d.length !== 8) return "";
  const dd = +d.slice(0, 2);
  const mm = +d.slice(2, 4);
  const yy = +d.slice(4);
  const date = new Date(yy, mm - 1, dd);
  const now = new Date();
  if (date.getFullYear() !== yy || date.getMonth() !== mm - 1 || date.getDate() !== dd) return "";
  if (date > now || yy < now.getFullYear() - 10) return "";
  return `${d.slice(4)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
};

// "HH:MM" -> "HH:MM", or "" when incomplete or impossible
export const parseHour = (text) => {
  const d = toDigits(text);
  if (d.length !== 4 || +d.slice(0, 2) > 23 || +d.slice(2) > 59) return "";
  return `${d.slice(0, 2)}:${d.slice(2)}`;
};

export const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim().toLowerCase());

export const isEmpty = (value) => {
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object" && value !== null) return Object.keys(value).length === 0;
  return value === undefined || value === null || value === "";
};

// First obligatory field (optionally among `only`) that is not filled; rules return true when filled
export const firstMissing = (obligatoryFields, rules, values, only) =>
  obligatoryFields.find(
    (field) => (!only || only.includes(field)) && !(rules[field] ? rules[field](values) : !isEmpty(values[field]))
  );

// { file, url } as kept in state -> File for FormData
export const asFile = ({ file }) => new File([file], file.name, { type: file.type, lastModified: file.lastModified });

// Same settings as components/ImageUploader.js
export const compressImage = (file) =>
  new Promise((resolve, reject) => {
    new Compressor(file, { quality: 0.6, maxWidth: 800, maxHeight: 800, mimeType: "image/jpeg", success: resolve, error: reject });
  });

// Checks and compresses the files of an <input type="file">; returns { items: [{ file, url }], error: i18n key }
export const readImages = async (input, max) => {
  const files = Array.from(input.files).slice(0, max);
  input.value = "";
  let error = "";
  const valid = files.filter((file) => {
    if (!file.type.startsWith("image/")) error = "rcBadFile";
    else if (sanitizeFilename(file.name) !== file.name) error = "rcBadFileName";
    else return true;
    return false;
  });
  try {
    const compressed = await Promise.all(valid.map(compressImage));
    return { items: compressed.map((file) => ({ file, url: URL.createObjectURL(file) })), error };
  } catch (err) {
    console.error("Error during compression:", err);
    return { items: [], error: "rcBadFile" };
  }
};

// Wilayas and communes from public/data
export function useLocations() {
  const [wilayas, setWilayas] = useState([]);
  const [communes, setCommunes] = useState([]);
  useEffect(() => {
    const load = (file, set) =>
      fetch(`${dataLink}/data/${file}`)
        .then((response) => (response.ok ? response.json() : Promise.reject(response.statusText)))
        .then(set)
        .catch((error) => console.error(`Error fetching ${file}:`, error));
    load("wilayas.json", setWilayas);
    load("communes.json", setCommunes);
  }, []);
  return { wilayas, communes };
}

// Drawer state: the first unfilled step is open, unless the user reopened a filled one
export function useDrawers(steps) {
  const [done, setDone] = useState({}); // { [step]: summary }
  const [openStep, setOpenStep] = useState(null);
  const firstTodo = steps.find((step) => !(step in done));
  const activeStep = steps.includes(openStep) ? openStep : firstTodo;
  return {
    done,
    firstTodo,
    activeStep,
    open: setOpenStep,
    toggle: (step) => setOpenStep(activeStep === step ? null : step),
    close: (step, summary) => {
      setDone((prev) => ({ ...prev, [step]: summary }));
      setOpenStep(null);
    },
    reset: (...resetSteps) => setDone((prev) => Object.fromEntries(Object.entries(prev).filter(([step]) => !resetSteps.includes(step)))),
  };
}

// ── Section animation ──────────────────────────────────────────────────────────
// Same rules as the prototype ("slide" version): the filled section closes while the next
// one opens, over the same duration and curve, so the page height goes straight from the
// old one to the new one. The old content fades in the first half; the new card does not
// move, it is unveiled from one edge to the other ("balayage" in the prototype).

const DURATION = 320;
const EASE = "cubic-bezier(.4,0,.2,1)";

const noMotion = () => !Element.prototype.animate || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const stopAnims = (el) => el?.getAnimations?.().forEach((a) => a.cancel());

export function Reveal({ open, children }) {
  const [rendered, setRendered] = useState(open);
  const blockRef = useRef(null);
  const innerRef = useRef(null);
  const prevOpen = useRef(open); // a section already open when the page is displayed does not animate

  if (open && !rendered) setRendered(true);

  useLayoutEffect(() => {
    const block = blockRef.current;
    const inner = innerRef.current;
    const changed = prevOpen.current !== open;
    prevOpen.current = open;
    if (!block || !changed) return;
    stopAnims(block);
    stopAnims(inner);
    block.classList.remove(styles.isAnim);

    if (noMotion()) {
      if (!open) setRendered(false);
      return;
    }
    if (open) {
      // unveiled from the reading side
      const from = block.closest("[dir]")?.dir === "rtl" ? "inset(0 0 0 100% round 16px)" : "inset(0 100% 0 0 round 16px)";
      const height = block.offsetHeight;
      block.classList.add(styles.isAnim);
      const anim = block.animate([{ height: "0px" }, { height: `${height}px` }], { duration: DURATION, easing: EASE });
      anim.onfinish = () => {
        block.classList.remove(styles.isAnim);
        // scroll only if the open section goes off screen
        const rect = block.getBoundingClientRect();
        if (rect.bottom > window.innerHeight - 12 || rect.top < 8) block.scrollIntoView({ behavior: "smooth", block: "nearest" });
      };
      inner.animate([{ clipPath: from, opacity: 0.5 }, { clipPath: "inset(0 0 0 0 round 16px)", opacity: 1 }], {
        duration: DURATION * 0.8,
        delay: DURATION * 0.2,
        easing: EASE,
        fill: "backwards",
      });
    } else {
      const height = block.offsetHeight;
      block.classList.add(styles.isAnim);
      // the height shrinks over the whole duration; the content fades in the first half
      // fill: the block stays collapsed until React removes it, instead of popping back for a frame
      const anim = block.animate([{ height: `${height}px` }, { height: "0px" }], { duration: DURATION, easing: EASE, fill: "forwards" });
      inner.animate([{ opacity: 1 }, { opacity: 0 }], { duration: DURATION * 0.45, easing: "ease-out", fill: "forwards" });
      anim.onfinish = () => setRendered(false);
    }
  }, [open, rendered]);

  if (!rendered) return null;
  return (
    <div ref={blockRef} className={styles.stepBlock}>
      <div ref={innerRef} className={styles.stepIn}>
        {children}
      </div>
    </div>
  );
}

// ── Layout ─────────────────────────────────────────────────────────────────────

// On phones the keyboard (or the select picker) shrinks the visible area and iOS pans it,
// while a position: fixed element keeps the full height: the page jumps and the top of a
// sheet goes off screen. The element is pinned to the visible area instead.
function useFitVisibleArea(ref) {
  useLayoutEffect(() => {
    const vv = window.visualViewport;
    const el = ref.current;
    if (!vv || !el) return;
    const fit = () => {
      // pinch zoom: leave the default layout
      if (Math.abs(vv.scale - 1) > 0.01) {
        el.style.top = el.style.height = el.style.bottom = "";
        return;
      }
      el.style.top = `${vv.offsetTop}px`;
      el.style.height = `${vv.height}px`;
      el.style.bottom = "auto";
    };
    fit();
    vv.addEventListener("resize", fit);
    vv.addEventListener("scroll", fit);
    return () => {
      vv.removeEventListener("resize", fit);
      vv.removeEventListener("scroll", fit);
    };
  }, [ref]);
}

// Page + card + module bar (back, title, language). `below` is rendered under the card.
// `done`, once set, takes the place of the form: the confirmation screen.
// `formBg`: the client's form background of production (--main-complaint_bg), on the pages that have it.
export function PageShell({ title, onBack, currentLang, setCurrentLang, noInstall, below, done, formBg, children }) {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const pageRef = useRef(null);
  const tenantName = useSelector((state) => state.tenant?.tenant.name) || "";
  const isDone = !!done;
  useFitVisibleArea(pageRef);

  // the confirmation is read from the top
  useEffect(() => {
    if (isDone) pageRef.current?.scrollTo(0, 0);
  }, [isDone]);

  return (
    // data-tenant: the client colours of the style guide (see _prototype.scss)
    <div ref={pageRef} className={classNames(styles.page, { [styles.formBg]: formBg })} data-tenant={tenantName}>
      {/* same layout in every language (client request): only the texts change */}
      <div className={styles.card} dir="ltr">
        {/* same places in every language: back on the left, title next to it, language on the right */}
        <div className={styles.cardHeader} dir="ltr">
          <button type="button" className={styles.headerBack} aria-label={t("rcBack")} title={t("rcBack")} onClick={onBack}>
            <ChevronLeft size={24} strokeWidth={2.4} aria-hidden />
          </button>
          <h1 className={styles.headerTitle} dir={dir}>
            {title}
          </h1>
          <LangButtons align="end" currentLang={currentLang} setCurrentLang={setCurrentLang} />
        </div>
        {done || children}
      </div>
      {/* also under the confirmation: "you can download the app" as its own card */}
      {!noInstall && (
        <div className={styles.below} dir="ltr">
          <InstallCard />
        </div>
      )}
      {below}
    </div>
  );
}

export const Question = ({ i18nKey, center }) => (
  <div className={classNames(styles.sectionQ, { [styles.center]: center })}>
    <Trans i18nKey={i18nKey} components={{ b: <b />, hl: <b /> }} />
  </div>
);

// Sending with a field left out: the page scrolls to that field, which shakes (and the phone
// vibrates where the browser allows it, not on iPhones). Fields say which checks they answer
// with data-field (the obligatory-field keys of the tenant config, or an error key); `delay` lets
// a step that was just reopened finish opening first.
export const flagField = (key, delay = 0) => {
  setTimeout(() => {
    const el = key && document.querySelector(`[data-field~="${key}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove(styles.flagged);
    void el.offsetWidth; // restarts the animation when the same field is flagged again
    el.classList.add(styles.flagged);
    try {
      navigator.vibrate?.([70, 50, 70]);
    } catch (_) {}
  }, delay);
};

// `field`: the checks this field answers (see flagField)
export function Field({ label, required, optional, hint, hintBad, field, children }) {
  const { t } = useTranslation();
  return (
    <label className={styles.field} data-field={field}>
      <span className={styles.fieldLabel}>
        {label}
        {required && <span className={styles.req}> *</span>}
        {optional && <span className={styles.opt}> {t("rcOptional")}</span>}
      </span>
      {children}
      {hint && <span className={classNames(styles.hint, { [styles.isBad]: hintBad })}>{hint}</span>}
    </label>
  );
}

export function Select({ value, onChange, placeholder, options, disabled, ...rest }) {
  return (
    <div className={styles.selectWrap}>
      <select className={styles.select} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} {...rest}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <ChevronDown className={styles.chevron} size={16} aria-hidden />
    </div>
  );
}

export const ErrorBox = ({ children }) =>
  children ? (
    <div className={styles.error} role="alert">
      {children}
    </div>
  ) : null;

// A filled step: one row, tap anywhere to reopen or close it. It grows in instead of popping up.
// `icon` (an emoji from the back office) replaces `Icon` when given
export function SummaryRow({ Icon, icon, text, sub, expanded, onClick }) {
  const { t } = useTranslation();
  const rowRef = useRef(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    if (noMotion()) return;
    const height = row.offsetHeight;
    const collapsed = { height: "0px", minHeight: "0px", paddingTop: "0px", paddingBottom: "0px", marginBottom: "0px", borderTopWidth: "0px", borderBottomWidth: "0px", opacity: 0 };
    const full = { height: `${height}px`, minHeight: `${height}px`, paddingTop: "7px", paddingBottom: "7px", marginBottom: "8px", borderTopWidth: "1.5px", borderBottomWidth: "1.5px", opacity: 1 };
    row.style.overflow = "hidden";
    const anim = row.animate([collapsed, full], { duration: DURATION, easing: EASE });
    anim.onfinish = anim.oncancel = () => {
      row.style.overflow = "";
    };
    return () => anim.cancel();
  }, []);

  return (
    <button ref={rowRef} type="button" className={styles.summaryRow} aria-expanded={expanded} onClick={onClick}>
      <span className={styles.summaryLeft}>
        <span className={styles.summaryIcon}>
          {icon ?? <Icon size={18} aria-hidden />}
        </span>
        <span>
          <span className={styles.summaryText}>
            {text}
            <svg className={styles.summaryCheck} width="17" height="17" viewBox="0 0 24 24" role="img" aria-label={t("rcDone")}>
              <circle cx="12" cy="12" r="11" fill="#16A34A" />
              <path d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className={styles.summarySub}>{sub}</span>
        </span>
      </span>
      <ChevronDown className={styles.summaryChev} size={20} strokeWidth={2.2} aria-hidden />
    </button>
  );
}

// One drawer: summary row once filled + animated body
// `fields`: the checks this step answers (see flagField)
export function Drawer({ drawers, step, Icon, icon, summary, sub, error, fields, children }) {
  return (
    <div data-field={fields}>
      {step in drawers.done && (
        <SummaryRow Icon={Icon} icon={icon} text={summary} sub={sub} expanded={drawers.activeStep === step} onClick={() => drawers.toggle(step)} />
      )}
      <Reveal open={drawers.activeStep === step}>
        {children}
        <ErrorBox>{drawers.activeStep === step && error}</ErrorBox>
      </Reveal>
    </div>
  );
}

// Bottom sheet
export function Sheet({ title, tall, onClose, children }) {
  const { t } = useTranslation();
  const overlayRef = useRef(null);
  useFitVisibleArea(overlayRef);
  return (
    <div ref={overlayRef} className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={classNames(styles.sheet, { [styles.tall]: tall })} role="dialog" aria-modal="true" dir="ltr">
        <div className={styles.sheetHandle} />
        <div className={styles.sheetHead}>
          <div className={styles.sheetTitle}>{title}</div>
          <button type="button" className={styles.sheetClose} aria-label={t("rcClose")} onClick={onClose}>
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className={styles.sheetBody}>{children}</div>
      </div>
    </div>
  );
}

// ── Install the app ────────────────────────────────────────────────────────────

// Same behaviour as components/InstallPrompt.js
// The install hook (hooks/usePwaInstall, not to be changed) cannot tell from a browser tab that the
// app is already installed. So the phone remembers it: when the browser reports the install
// ("pwa:installed", sent by public/index.html) or the user accepts the install prompt. When the
// browser offers to install again, the app was removed: forgotten.
const APP_INSTALLED_KEY = "jpAppInstalled";
const readAppInstalled = () => {
  try {
    return localStorage.getItem(APP_INSTALLED_KEY) === "1";
  } catch (_) {
    return false;
  }
};
const saveAppInstalled = (installed) => {
  try {
    if (installed) localStorage.setItem(APP_INSTALLED_KEY, "1");
    else localStorage.removeItem(APP_INSTALLED_KEY);
  } catch (_) {}
};

export function useInstallApp() {
  const { canPrompt, needsManualSteps, promptInstall, platform, isStandalone } = usePwaInstall();
  const [showIosModal, setShowIosModal] = useState(false);
  const [showAndroidModal, setShowAndroidModal] = useState(false);
  const [showInstalled, setShowInstalled] = useState(false);
  const needsAndroidSteps = platform === "other" && !isStandalone && !canPrompt;

  useEffect(() => {
    const onInstalled = () => saveAppInstalled(true);
    window.addEventListener("pwa:installed", onInstalled);
    return () => window.removeEventListener("pwa:installed", onInstalled);
  }, []);
  // the browser offers the install again: the app is not (or no longer) on the phone
  useEffect(() => {
    if (canPrompt) saveAppInstalled(false);
  }, [canPrompt]);

  const install = async () => {
    // already installed: say it instead of showing the install steps
    if (isStandalone || (!canPrompt && readAppInstalled())) return setShowInstalled(true);
    if (canPrompt) {
      const outcome = await promptInstall();
      if (outcome === "accepted") saveAppInstalled(true);
      if (outcome !== "accepted" && outcome !== "dismissed") setShowAndroidModal(true);
    } else if (needsManualSteps) {
      setShowIosModal(true);
    } else if (needsAndroidSteps) {
      setShowAndroidModal(true);
    }
  };

  const modals = (
    <>
      {showIosModal && <IosInstallModal onClose={() => setShowIosModal(false)} />}
      {showAndroidModal && <AndroidInstallModal onClose={() => setShowAndroidModal(false)} />}
      {showInstalled && <AppInstalledSheet onClose={() => setShowInstalled(false)} />}
    </>
  );
  return { canInstall: canPrompt || needsManualSteps || needsAndroidSteps, install, modals };
}

function AppInstalledSheet({ onClose }) {
  const { t } = useTranslation();
  return (
    <Sheet title={t("appAlreadyInstalledTitle")} onClose={onClose}>
      <div className={styles.installedMsg}>
        <span className={styles.installedIcon} aria-hidden="true">
          <Check size={26} strokeWidth={2.6} />
        </span>
        <p>{t("appAlreadyInstalled")}</p>
      </div>
      <button type="button" className={classNames(styles.next, styles.block)} onClick={onClose}>
        {t("rcGotIt")}
      </button>
    </Sheet>
  );
}

// Offer to install the app, shown under the form when it is not installed yet
function InstallCard() {
  const { t } = useTranslation();
  const { canInstall, install, modals } = useInstallApp();
  return (
    <>
      {canInstall && (
        <div className={styles.installCard}>
          {/* the icon installs too, like the button */}
          <button type="button" className={styles.installIcon} aria-label={t("oui")} onClick={install}>
            <Download size={20} aria-hidden />
          </button>
          <p className={styles.installText}>{t("installQuestion")}</p>
          <button type="button" className={styles.installBtn} onClick={install}>
            {t("oui")}
          </button>
        </div>
      )}
      {modals}
    </>
  );
}

// Confirmation shown in place of the form once it is sent (prototype "ok" screen): a whole screen
// of the card, with production's confetti. The offer to install the app is its own card under this
// one (see PageShell).
// `children`: shown under the text (the survey's participation number)
export function ThanksScreen({ title, text, onHome, children }) {
  const { t } = useTranslation();
  return (
    <div className={styles.okBody} role="status">
      <Confetti />
      <div className={styles.okCheck}>
        <svg width="96" height="96" viewBox="0 0 96 96" aria-hidden="true">
          <circle cx="48" cy="48" r="44" />
          <path d="M29 50l13 13 26-28" />
        </svg>
      </div>
      <div className={styles.okTitle}>{title}</div>
      {text && <p className={styles.okText}>{text}</p>}
      {children}
      <button type="button" className={classNames(styles.next, styles.block)} onClick={onHome}>
        {t("rcBackHome")}
      </button>
    </div>
  );
}

// ── Example sheets ─────────────────────────────────────────────────────────────
// The first time the user touches the field, an example shows where to read the information
// on the packaging. The field then works normally; the "?" button reopens the example.

const guideSeen = {}; // { code, lot }: once per visit

function useGuide(kind) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  return {
    open,
    show: () => {
      guideSeen[kind] = true;
      setOpen(true);
    },
    // onFocus of the guided inputs
    onFirstFocus: (e) => {
      if (guideSeen[kind]) return;
      guideSeen[kind] = true;
      inputRef.current = e.target;
      e.target.blur();
      setOpen(true);
    },
    // on closing, the field takes over again and the keyboard opens
    close: () => {
      setOpen(false);
      inputRef.current?.focus();
      inputRef.current = null;
    },
  };
}

const HelpButton = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <button type="button" className={styles.help} aria-label={t("rcSeeExample")} title={t("rcSeeExample")} onClick={onClick}>
      <CircleHelp size={20} aria-hidden />
    </button>
  );
};

// `image`: the lot example of the chosen product (product.group.img from the backend, a real photo of
// its packaging); without one, the drawn example and its numbered legend
function GuideSheet({ kind, image, onClose }) {
  const { t } = useTranslation();
  const isLot = kind === "lot";
  const photo = isLot && image;
  return (
    <Sheet title={<Trans i18nKey={isLot ? "rcGuideLotTitle" : "rcGuideCodeTitle"} components={{ b: <b /> }} />} onClose={onClose}>
      {photo ? (
        <div className={styles.guidePhotoWrap}>
          <img className={styles.guidePhoto} src={image} alt="" />
        </div>
      ) : (
        <div className={styles.guideFig} dangerouslySetInnerHTML={{ __html: isLot ? GUIDE_LOT_SVG : GUIDE_CODE_SVG }} />
      )}
      <p className={styles.guideText}>{t(isLot ? "rcGuideLotText" : "rcGuideCodeText")}</p>
      {isLot && !photo && (
        <>
          <div className={styles.guideLeg}>
            <span className={classNames(styles.guideNum, styles.n1)}>1</span>
            <span>
              <Trans i18nKey="rcGuideLegendLot" components={{ b: <b dir="ltr" /> }} />
            </span>
          </div>
          <div className={styles.guideLeg}>
            <span className={classNames(styles.guideNum, styles.n2)}>2</span>
            <span>
              <Trans i18nKey="rcGuideLegendFab" components={{ b: <b dir="ltr" /> }} />
            </span>
          </div>
        </>
      )}
      <button type="button" className={classNames(styles.next, styles.block)} onClick={onClose}>
        {t("rcGotIt")}
      </button>
    </Sheet>
  );
}

// ── Product ────────────────────────────────────────────────────────────────────

// Brand order of the product list, per client (client request); names compared without spaces or case
const BRAND_ORDER = {
  palmary: ["MAXON", "KOOL", "MOMENT", "HAPPY", "REGALO", "ELTADJ", "BONO", "SOLTANA", "PALMARY", "MAGMA", "BIBILA", "LPF"],
};
const brandKey = (name) => normalize(name).replace(/[\s-]/g, "");

// Product list: search + brand filter, compact rows
function ProductSheet({ products, onPick, onClose }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const tenantName = useSelector((state) => state.tenant?.tenant.name) || "";
  const [search, setSearch] = useState("");
  const [brandId, setBrandId] = useState(null);

  // brands in the client's order when it has one, the others after in their usual order
  const brands = useMemo(() => {
    const byId = new Map();
    products.forEach((p) => p.brand?.name && byId.set(p.brand.id, p.brand.name));
    const list = [...byId].map(([id, name]) => ({ id, name }));
    const order = (BRAND_ORDER[tenantName] || []).map(brandKey);
    const rank = (brand) => {
      const at = order.indexOf(brandKey(brand.name));
      return at === -1 ? order.length : at;
    };
    return list.map((brand, at) => ({ ...brand, at })).sort((a, b) => rank(a) - rank(b) || a.at - b.at);
  }, [products, tenantName]);

  const rows = useMemo(() => {
    const query = normalize(search);
    return products.filter(
      (p) =>
        (brandId === null || p.brand?.id === brandId) &&
        (!query || [p.name, p.arabic_translation, p.english_translation].some((n) => normalize(n).includes(query)))
    );
  }, [products, search, brandId]);

  return (
    <Sheet tall title={<Trans i18nKey="rcSheetProduct" components={{ b: <b /> }} />} onClose={onClose}>
      {/* same places in every language (search, brands from the left, name then brand); only the texts change */}
      <div className={styles.plSearch} dir="ltr">
        <Search size={18} aria-hidden />
        <input
          type="search"
          className={styles.input}
          autoComplete="off"
          placeholder={t("rcSearchPh")}
          aria-label={t("rcSearchPh")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {brands.length > 1 && (
        <div className={styles.plFilters} dir="ltr">
          {[{ id: null, name: t("rcAll") }, ...brands].map((b) => (
            <button
              type="button"
              key={b.id ?? "all"}
              aria-pressed={brandId === b.id}
              className={classNames(styles.chip, { [styles.isSelected]: brandId === b.id })}
              onClick={() => setBrandId(b.id)}
            >
              {b.name}
            </button>
          ))}
        </div>
      )}
      <div className={styles.plCount} aria-live="polite">
        {t("rcCount", { n: rows.length })}
      </div>
      <div className={styles.plList} dir="ltr">
        {rows.length === 0 && <div className={styles.plEmpty}>{t("rcEmpty")}</div>}
        {rows.map((p) => (
          <button type="button" key={p.id} className={styles.plRow} onClick={() => onPick(p)}>
            <span className={styles.plName} dir="auto">
              {localized(p, lang)}
            </span>
            {brands.length > 1 && (
              <span className={styles.plCat} dir="auto">
                {p.brand?.name}
              </span>
            )}
          </button>
        ))}
      </div>
    </Sheet>
  );
}

// Camera scanner (tenants with scannerModule): the new-design copy of production's modal,
// loaded only when it is opened
const ScanModal = lazy(() => import("./ScanModal"));

// Short beep when a scanned barcode matches a product (same sound as components/ProductCodeEntry.jsx).
// iPhones only play sound from an audio context started by a tap: the scan button's tap starts it
// (unlockBeep) and the beep, which comes later from the camera, reuses it.
let beepCtx = null;
const unlockBeep = () => {
  try {
    if (!beepCtx) beepCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (beepCtx.state === "suspended") beepCtx.resume();
    // a silent sound during the tap is what unlocks it on iOS
    const source = beepCtx.createBufferSource();
    source.buffer = beepCtx.createBuffer(1, 1, 22050);
    source.connect(beepCtx.destination);
    source.start(0);
  } catch (_) {}
};
const playBeep = () => {
  try {
    if (!beepCtx) beepCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = beepCtx;
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(1800, ctx.currentTime);
    gain.gain.setValueAtTime(0.4, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.18);
  } catch (_) {}
};

// Small dark tip shown on first use only, like the voice one (product and lot fields): remembered on
// the phone after "Compris", or for the visit when storage is blocked. Same layout in every language.
const tipsSeen = new Set();
const readTipSeen = (storageKey) => {
  try {
    return tipsSeen.has(storageKey) || localStorage.getItem(storageKey) === "1";
  } catch (_) {
    return tipsSeen.has(storageKey);
  }
};
export function FirstUseTip({ storageKey, text }) {
  const { t, i18n } = useTranslation();
  const [seen, setSeen] = useState(() => readTipSeen(storageKey));
  if (seen) return null;
  const hide = () => {
    try {
      localStorage.setItem(storageKey, "1");
    } catch (_) {}
    tipsSeen.add(storageKey);
    setSeen(true);
  };
  return (
    <div className={classNames(styles.wcCoach, styles.codeTip)} dir="ltr">
      <span dir={i18n.language === "ar" ? "rtl" : "ltr"}>{text}</span>
      <button type="button" onClick={hide}>
        {t("rcWcOk")}
      </button>
    </div>
  );
}

// Last 6 digits of the barcode + product list; tenants with scannerModule also get the camera scanner.
// `onAll`, when given, adds the "all products" choice of the merchant form.
export function ProductPicker({ productsList, scanner, onPick, onAll }) {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [sheetProducts, setSheetProducts] = useState(null); // products listed in the bottom sheet
  const [scanning, setScanning] = useState(false);
  const guide = useGuide("code");
  const onCodeChangeRef = useRef(null);

  /** Several products can share the same last 6 digits: the user then picks one in the list.
   *  `scanned`: the digits come from the camera, a found product beeps */
  const onCodeChange = (value, scanned = false) => {
    const digits = toDigits(value).slice(0, 6);
    setCode(digits);
    setCodeError("");
    if (digits.length < 6) return;
    const matches = productsList.filter((item) => item.barcode && item.barcode.slice(-6) === digits);
    if (scanned && matches.length > 0) playBeep();
    if (matches.length === 1) {
      setTimeout(() => onPick(matches[0]), 350);
    } else if (matches.length > 1) {
      setCode("");
      setSheetProducts(matches);
    } else {
      setCode("");
      setCodeError(`${t("prodNotFound")} ${t("reEnterCode")}`);
    }
  };
  onCodeChangeRef.current = onCodeChange;

  // the modal restarts its camera when these change: keep them stable
  const closeScan = useCallback(() => setScanning(false), []);
  const typeInstead = useCallback(() => {
    setScanning(false);
    setTimeout(() => document.getElementById("rcCode")?.focus(), 100);
  }, []);
  const onScanned = useCallback(
    (text) => {
      setScanning(false);
      const digits = toDigits(text).slice(-6);
      if (digits.length === 6) return onCodeChangeRef.current(digits, true);
      setCode("");
      setCodeError(`${t("prodNotFound")} ${t("reEnterCode")}`);
    },
    [t]
  );

  return (
    <>
      {/* first use only: what to type here */}
      <FirstUseTip storageKey="jpCodeTipSeen" text={t("rcCodeTip")} />
      {/* same places in every language: "?" on the right, then field, scan, list; only the texts change */}
      <div className={classNames(styles.field, styles.hasHelp, styles.centered)} dir="ltr">
        <label className={styles.fieldLabel} htmlFor="rcCode" dir={dir}>
          {t("rcCodeLabel")}
        </label>
        <HelpButton onClick={guide.show} />
        <div className={classNames(styles.inline, styles.codeRow)}>
          <input
            id="rcCode"
            type="text"
            className={classNames(styles.input, styles.code, { [styles.isOk]: code.length === 6 })}
            inputMode="numeric"
            autoComplete="off"
            maxLength="6"
            placeholder="000000"
            value={code}
            onFocus={guide.onFirstFocus}
            onChange={(e) => onCodeChange(e.target.value)}
          />
          {scanner && (
            <button
              type="button"
              className={classNames(styles.iconBtn, styles.scanBtn)}
              aria-label={t("rcScanTitle")}
              title={t("rcScanTitle")}
              onClick={() => {
                unlockBeep();
                setScanning(true);
              }}
            >
              <ScanBarcode size={22} aria-hidden />
            </button>
          )}
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={t("rcListBtn")}
            title={t("rcListBtn")}
            onClick={() => setSheetProducts(productsList)}
          >
            <List size={22} aria-hidden />
          </button>
        </div>
        <div className={classNames(styles.hint, { [styles.isBad]: codeError })} role={codeError ? "alert" : undefined} dir={dir}>
          {codeError || t(scanner ? "rcCodeHintScan" : "rcCodeHint")}
        </div>
      </div>
      {onAll && (
        <button type="button" className={styles.tagBtn} style={{ marginTop: 14 }} onClick={onAll}>
          {t("rmAllProductsClaim")}
        </button>
      )}

      {sheetProducts && (
        <ProductSheet
          products={sheetProducts}
          onClose={() => setSheetProducts(null)}
          onPick={(product) => {
            setSheetProducts(null);
            onPick(product);
          }}
        />
      )}
      {/* a read barcode is matched on its last 6 digits, like typed ones */}
      {scanning && (
        <Suspense fallback={null}>
          <ScanModal onClose={closeScan} onScanned={onScanned} onSwitchToManual={typeInstead} />
        </Suspense>
      )}
      {/* Example: where to find the barcode digits on the packaging */}
      {guide.open && <GuideSheet kind="code" onClose={guide.close} />}
    </>
  );
}

// ── Problem ────────────────────────────────────────────────────────────────────

// At most `max` types (two by default): picking one more replaces the oldest
export const toggleType = (selected, id, max = MAX_TYPES) => {
  if (selected.includes(id)) return selected.filter((x) => x !== id);
  return selected.length >= max ? [...selected.slice(selected.length - max + 1), id] : [...selected, id];
};

// The backend sends each complaint type's icon (type.icon): an emoji, or a link to a picture.
// A type without one shows its name only.
const isImageIcon = (icon) => typeof icon === "string" && /^(https?:|\/|data:image)/.test(icon.trim());
const hasIcon = (icon) => typeof icon === "string" && icon.trim() !== "";
// "Autre" without an icon from the backend: a circle with three dots (client's icon)
const isOtherType = (type) =>
  ["autre", "other", "أخرى", "آخر"].some((v) => [type?.name, type?.arabic_translation, type?.english_translation].some((n) => n?.trim().toLowerCase() === v));

// The first VISIBLE_TYPES tiles are shown, then a "see n more choices" button (as in the prototype)
const VISIBLE_TYPES = 8;

// A selected type is filled with the brand colour; tapping it again removes the colour.
// `loading`: placeholder tiles while the types arrive, so the step opens at its real height
// instead of opening empty and growing when the list comes in.
export function TypeGrid({ types, loading, selected, onToggle }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // a selected type past the first tiles keeps the list open (e.g. when the step is reopened)
  const hiddenSelected = types.slice(VISIBLE_TYPES).some((type) => selected.includes(type.id));
  const showAll = expanded || hiddenSelected;
  const hiddenCount = Math.max(0, types.length - VISIBLE_TYPES);

  if (loading && types.length === 0) {
    return (
      <div className={classNames(styles.iconGrid, styles.cols4, styles.typeTiles)} aria-busy="true">
        {Array.from({ length: VISIBLE_TYPES }, (_, index) => (
          <span key={index} className={classNames(styles.igBtn, styles.igSkeleton)} />
        ))}
      </div>
    );
  }
  return (
    <>
      <div className={classNames(styles.iconGrid, styles.cols4, styles.typeTiles)}>
        {types.map((type, index) =>
          showAll || index < VISIBLE_TYPES ? (
            <button
              type="button"
              key={type.id}
              aria-pressed={selected.includes(type.id)}
              className={classNames(styles.igBtn, { [styles.isSelected]: selected.includes(type.id) })}
              onClick={() => onToggle(type.id)}
            >
              {hasIcon(type.icon) ? (
                <span className={styles.igIcon} aria-hidden="true">
                  {isImageIcon(type.icon) ? <img src={type.icon} alt="" loading="lazy" /> : type.icon}
                </span>
              ) : (
                isOtherType(type) && (
                  <span className={styles.igIcon} aria-hidden="true">
                    <CircleEllipsis className={styles.igIconSvg} strokeWidth={2.2} />
                  </span>
                )
              )}
              <span className={styles.igLabel}>{localized(type, i18n.language)}</span>
            </button>
          ) : null
        )}
      </div>
      {hiddenCount > 0 && !hiddenSelected && (
        <button type="button" className={classNames(styles.ghost, styles.more)} aria-expanded={showAll} onClick={() => setExpanded(!expanded)}>
          <span>{showAll ? t("alertsLess") : t("rcMore", { n: hiddenCount })}</span>
          <ChevronDown size={18} strokeWidth={2.2} aria-hidden />
        </button>
      )}
    </>
  );
}

// "Continuer" sits on the right of its section, in every language
export const NextButton = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <div className={classNames(styles.actions, styles.actionsRight)}>
      <button type="button" className={styles.next} onClick={onClick}>
        {t("rcNext")}
      </button>
    </div>
  );
};

// ── Lot ────────────────────────────────────────────────────────────────────────

export const EMPTY_LOT = { lot: "", photo: null, dateText: "", hourText: "" };
// what a skipped lot step remembers ("I no longer have the packaging")
export const LOT_SKIPPED = "skipped";

// Summary of the lot step, shown in its row once filled
export const lotSummary = (value, t) => {
  const parts = [];
  if (value.lot.trim()) parts.push(t("rcLotPrefix") + value.lot.trim());
  else if (value.photo) parts.push(t("rcLotPhotoSum"));
  if (parseDate(value.dateText)) parts.push(t("rcProducedOn") + value.dateText + (parseHour(value.hourText) ? ` ${parseHour(value.hourText)}` : ""));
  return parts.join(" · ");
};

// Lot number (typed or photographed), production date and, for tenants that ask for it, hour.
// `value` is { lot, photo: { file, url } | null, dateText, hourText }.
// `lotImage`: photo of where the lot is printed on the chosen product (product.group.img)
export function LotFields({ value, onChange, show, isRequired, lotPlaceholder, lotFilter, onBusy, lotImage }) {
  const { t } = useTranslation();
  const [dateTouched, setDateTouched] = useState(false);
  const [fileError, setFileError] = useState("");
  const guide = useGuide("lot");
  const photoSource = usePhotoSource({ multiple: false, onChange: (e) => addPhoto(e) });
  const set = (patch) => onChange({ ...value, ...patch });

  const dateBad = toDigits(value.dateText).length === 8 ? !parseDate(value.dateText) : dateTouched && value.dateText !== "";

  const addPhoto = async (e) => {
    onBusy?.(true);
    const { items, error } = await readImages(e.target, 1);
    onBusy?.(false);
    setFileError(error);
    if (!items.length) return;
    if (value.photo) URL.revokeObjectURL(value.photo.url);
    set({ photo: items[0] });
  };

  const removePhoto = () => {
    URL.revokeObjectURL(value.photo.url);
    set({ photo: null });
  };

  return (
    <>
      {/* first use only: what to fill in here */}
      <FirstUseTip storageKey="jpLotTipSeen" text={t("rcLotTip")} />
      <div className={classNames(styles.field, styles.hasHelp)}>
        {show.lot && (
          <label className={styles.fieldLabel} htmlFor="rcLot">
            {t("rcLotLabel")}
            {isRequired.lot && <span className={styles.req}> *</span>}
          </label>
        )}
        <HelpButton onClick={guide.show} />
        <div className={styles.inline}>
          {show.lot && (
            <input
              id="rcLot"
              type="text"
              className={classNames(styles.input, styles.ltr)}
              autoComplete="off"
              maxLength="10"
              placeholder={lotPlaceholder}
              value={value.lot}
              onFocus={guide.onFirstFocus}
              // lotFilter: the characters the previous form removed
              onChange={(e) => set({ lot: xss(e.target.value.replace(lotFilter, "")).slice(0, 10) })}
            />
          )}
          <button type="button" className={classNames(styles.ghost, { [styles.isOn]: value.photo })} onClick={photoSource.openSource}>
            <Camera size={17} strokeWidth={1.8} aria-hidden />
            <span>{value.photo ? t("rcPhotoOn") : t("rcPhoto")}</span>
          </button>
          {photoSource.element}
        </div>
        <div className={classNames(styles.hint, { [styles.isBad]: fileError })}>{fileError ? t(fileError) : t("rcLotHint")}</div>
        {value.photo && (
          <div className={styles.thumbs}>
            <div className={styles.thumb}>
              <img src={value.photo.url} alt="" />
              <button type="button" className={styles.thumbRemove} aria-label={t("rcRemovePhoto")} onClick={removePhoto}>
                <X size={14} aria-hidden />
              </button>
            </div>
          </div>
        )}
      </div>
      {show.date && (
        <div className={show.hour ? styles.twoCol : undefined}>
          <Field label={t("rcDateLabel")} required={isRequired.date} hint={dateBad ? t("rcDateBad") : t("rcDateHint")} hintBad={dateBad}>
            <input
              type="text"
              className={classNames(styles.input, styles.date, { [styles.isBad]: dateBad })}
              inputMode="numeric"
              autoComplete="off"
              maxLength="10"
              placeholder={t("rcDatePh")}
              value={value.dateText}
              onFocus={guide.onFirstFocus}
              onBlur={() => setDateTouched(true)}
              onChange={(e) => {
                const d = toDigits(e.target.value).slice(0, 8);
                set({ dateText: d.slice(0, 2) + (d.length > 2 ? "/" : "") + d.slice(2, 4) + (d.length > 4 ? "/" : "") + d.slice(4) });
              }}
            />
          </Field>
          {show.hour && (
            <Field label={t("rcHourLabel")} required={isRequired.hour}>
              <input
                type="text"
                className={classNames(styles.input, styles.date)}
                inputMode="numeric"
                autoComplete="off"
                maxLength="5"
                placeholder="HH:MM"
                value={value.hourText}
                onChange={(e) => {
                  const d = toDigits(e.target.value).slice(0, 4);
                  set({ hourText: d.slice(0, 2) + (d.length > 2 ? ":" : "") + d.slice(2) });
                }}
              />
            </Field>
          )}
        </div>
      )}
      {/* Example: where to find the lot number and the date on the packaging */}
      {guide.open && <GuideSheet kind="lot" image={lotImage} onClose={guide.close} />}
    </>
  );
}

// ── Photo source: camera or gallery ────────────────────────────────────────────
// A photo button first asks where the photo comes from: "Prendre une photo" opens the camera
// directly (capture), "Choisir des photos" opens the gallery. On Android a plain file input
// offers only the gallery, and a capture input offers only the camera.
export function usePhotoSource({ multiple, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const pick = (ref) => {
    setOpen(false);
    ref.current?.click(); // still inside the tap: the browser lets it open the picker
  };
  const element = (
    <>
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={onChange} />
      <input ref={galleryRef} type="file" accept="image/*" multiple={multiple} hidden onChange={onChange} />
      {open && (
        <Sheet title={t("rcPhoto")} onClose={() => setOpen(false)}>
          <div className={styles.photoSource}>
            <button type="button" className={classNames(styles.ghost, styles.block)} onClick={() => pick(cameraRef)}>
              <Camera size={20} strokeWidth={1.8} aria-hidden />
              <span>{t("rcWcCamera")}</span>
            </button>
            <button type="button" className={classNames(styles.ghost, styles.block)} onClick={() => pick(galleryRef)}>
              <ImageIcon size={20} strokeWidth={1.8} aria-hidden />
              <span>{t("rcWcGallery")}</span>
            </button>
          </div>
        </Sheet>
      )}
    </>
  );
  return { openSource: () => setOpen(true), element };
}

// ── Comment, photos, voice message ─────────────────────────────────────────────
// The prototype's message box (WhatsApp style): one bar with the text and a camera button, a mic
// button next to it, thumbnails and a voice bubble above. Production rules: 2 photos (compressed as
// before), 30 s of voice recorded as one webm file, obligatory fields from the tenant config.

const MAX_VOICE_SECONDS = 30;
const formatSeconds = (x) => {
  const n = Math.max(0, Math.round(x));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};
// fixed pseudo-random bar heights for the voice bubble (same drawing as the prototype)
const WAVE_BARS = (() => {
  const out = [];
  let x = 11;
  for (let i = 0; i < 28; i += 1) {
    x = (x * 9301 + 49297) % 233280;
    out.push(5 + Math.round((x / 233280) * 18));
  }
  return out;
})();

let coachSeen = false; // the "Nouveau : …vocal…" tip, once per visit

// `show` / `isRequired` are { comment, photos, voice }; photos are [{ file, url }]; `bad` shakes the box
export function MessageBox({ show, comment, setComment, photos, setPhotos, setVoice, onBusy, bad }) {
  const { t } = useTranslation();
  const [fileError, setFileError] = useState("");
  const [rec, setRec] = useState(null); // { t, bars } while recording
  const [voice, setVoiceState] = useState(null); // { file, url, dur }
  const [playing, setPlaying] = useState(0); // 0..1 progress while playing, 0 when stopped
  const [touched, setTouched] = useState(false);
  const [, forceCoach] = useState(0);
  const boxRef = useRef(null);
  const textRef = useRef(null);
  const recRef = useRef(null); // { stream, recorder, chunks, ctx, analyser, timer, live, started, keep }
  const photoSource = usePhotoSource({ multiple: true, onChange: (e) => addPhotos(e) });
  const audioRef = useRef(null);

  const count = (comment.trim() ? 1 : 0) + (voice ? 1 : 0) + photos.length;

  // the box shakes when the form is sent with a required part missing
  useEffect(() => {
    if (!bad || !boxRef.current) return;
    const box = boxRef.current;
    box.classList.remove(styles.wcBad);
    void box.offsetWidth;
    box.classList.add(styles.wcBad);
  }, [bad]);

  // the red border goes away as soon as the user adds something
  useEffect(() => {
    boxRef.current?.classList.remove(styles.wcBad);
  }, [count]);

  // the text area grows with its content, up to 120px
  useLayoutEffect(() => {
    const ta = textRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    if (ta.scrollHeight) ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [comment, rec]);

  const stopPlay = () => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(0);
  };

  const cleanupRec = () => {
    const r = recRef.current;
    if (!r) return;
    clearInterval(r.timer);
    clearTimeout(r.live);
    r.stream?.getTracks().forEach((track) => track.stop());
    try {
      r.ctx?.close();
    } catch (_) {}
    recRef.current = null;
  };

  // stops everything when the page goes away
  useEffect(
    () => () => {
      const r = recRef.current;
      if (r) r.keep = false;
      if (r?.recorder && r.recorder.state !== "inactive") r.recorder.stop();
      cleanupRec();
      audioRef.current?.pause();
    },
    []
  );

  const stopRec = (keep) => {
    const r = recRef.current;
    if (!r) return;
    r.keep = keep;
    clearInterval(r.timer);
    clearTimeout(r.live);
    if (r.recorder && r.recorder.state !== "inactive") r.recorder.stop(); // onstop keeps the file
    else cleanupRec();
    setRec(null);
  };

  const startRec = async () => {
    stopPlay();
    setTouched(true);
    coachSeen = true;
    const r = { chunks: [], started: Date.now(), keep: false };
    recRef.current = r;
    setRec({ t: 0, bars: [] });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (recRef.current !== r) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      r.stream = stream;
      const mimeType = window.MediaRecorder?.isTypeSupported?.("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : undefined;
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      r.recorder = recorder;
      recorder.ondataavailable = (ev) => ev.data.size && r.chunks.push(ev.data);
      recorder.onstop = () => {
        const seconds = (Date.now() - r.started) / 1000;
        if (r.keep && seconds >= 1 && r.chunks.length) {
          const type = recorder.mimeType || "audio/webm";
          const timestamp = Date.now();
          // same file as components/AudioRecorder.js
          const file = new File([new Blob(r.chunks, { type })], `recording_${timestamp}.webm`, { type, lastModified: timestamp });
          // a new recording replaces the previous one (one voice message, as before)
          setVoiceState((prev) => {
            if (prev) URL.revokeObjectURL(prev.url);
            return { file, url: URL.createObjectURL(file), dur: Math.min(MAX_VOICE_SECONDS, Math.round(seconds)) };
          });
          setVoice([file]);
        }
        cleanupRec();
      };
      recorder.start();
      try {
        r.ctx = new (window.AudioContext || window.webkitAudioContext)();
        r.analyser = r.ctx.createAnalyser();
        r.analyser.fftSize = 256;
        r.ctx.createMediaStreamSource(stream).connect(r.analyser);
      } catch (_) {}
      r.timer = setInterval(() => {
        const elapsed = Math.floor((Date.now() - r.started) / 1000);
        setRec((prev) => prev && { ...prev, t: elapsed });
        if (elapsed >= MAX_VOICE_SECONDS) stopRec(true);
      }, 1000);
      // live bars from the microphone level
      const live = () => {
        if (recRef.current !== r) return;
        let v = 5 + Math.random() * 16;
        if (r.analyser) {
          const d = new Uint8Array(r.analyser.fftSize);
          r.analyser.getByteTimeDomainData(d);
          let m = 0;
          for (let i = 0; i < d.length; i += 1) m = Math.max(m, Math.abs(d[i] - 128));
          v = 4 + Math.min(22, m / 2.2);
        }
        setRec((prev) => prev && { ...prev, bars: [...prev.bars, v].slice(-34) });
        r.live = setTimeout(live, 90);
      };
      live();
    } catch (error) {
      console.error("Error accessing microphone:", error);
      cleanupRec();
      setRec(null);
      alert("Microphone access denied"); // same message as components/AudioRecorder.js
    }
  };

  const removeVoice = () => {
    stopPlay();
    if (voice) URL.revokeObjectURL(voice.url);
    setVoiceState(null);
    setVoice([]);
  };

  const togglePlay = () => {
    if (audioRef.current) return stopPlay();
    const audio = new Audio(voice.url);
    audioRef.current = audio;
    audio.ontimeupdate = () => setPlaying(Math.max(0.001, audio.currentTime / (audio.duration && isFinite(audio.duration) ? audio.duration : voice.dur)));
    audio.onended = stopPlay;
    setPlaying(0.001);
    audio.play().catch(stopPlay);
  };

  const addPhotos = async (e) => {
    setTouched(true);
    onBusy?.(true);
    const { items, error } = await readImages(e.target, MAX_PHOTOS - photos.length);
    onBusy?.(false);
    setFileError(error);
    setPhotos((prev) => [...prev, ...items].slice(0, MAX_PHOTOS));
  };

  const removePhoto = (url) => {
    URL.revokeObjectURL(url);
    setPhotos((prev) => prev.filter((p) => p.url !== url));
  };

  const playedBars = Math.round(playing * WAVE_BARS.length);
  const remaining = voice ? voice.dur - playing * voice.dur : 0;

  return (
    <div ref={boxRef} className={styles.wcBox} data-field="comment img_r voice_r">
      {show.voice && !coachSeen && !count && !rec && (
        <div className={styles.wcCoach}>
          <span>
            <Trans i18nKey="rcWcCoach" components={{ b: <b /> }} />
          </span>
          <button
            type="button"
            onClick={() => {
              coachSeen = true;
              forceCoach((n) => n + 1);
            }}
          >
            {t("rcWcOk")}
          </button>
        </div>
      )}

      {(photos.length > 0 || voice) && (
        <div className={styles.wcAtts}>
          {photos.length > 0 && (
            <div className={styles.wcThumbs}>
              {photos.map((photo) => (
                <div className={styles.wcTh} key={photo.url}>
                  <img src={photo.url} alt="" />
                  <button type="button" className={styles.wcX} aria-label={t("rcWcRemove")} onClick={() => removePhoto(photo.url)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {voice && (
            <div className={styles.wcVoice}>
              <button type="button" className={styles.wcPlay} aria-label={t("rcWcPlay")} onClick={togglePlay}>
                {playing ? <Pause size={14} fill="currentColor" strokeWidth={0} aria-hidden /> : <Play size={14} fill="currentColor" strokeWidth={0} aria-hidden />}
              </button>
              <span className={styles.wcWave}>
                {WAVE_BARS.map((h, i) => (
                  <i key={i} className={classNames({ [styles.on]: i < playedBars })} style={{ height: h }} />
                ))}
              </span>
              <span className={styles.wcDur}>{formatSeconds(playing ? remaining : voice.dur)}</span>
              <button type="button" className={styles.wcRm} aria-label={t("rcWcRemove")} onClick={removeVoice}>
                ×
              </button>
            </div>
          )}
        </div>
      )}

      {/* same places in every language: text bar (camera at its end) on the left, mic on the right */}
      <div className={styles.wcComp} dir="ltr">
        {rec ? (
          <div className={styles.wcRec}>
            <span className={styles.wcDot} />
            <span className={styles.wcTime}>{formatSeconds(rec.t)}</span>
            <span className={styles.wcLive}>
              {rec.bars.map((h, i) => (
                <i key={i} style={{ height: h }} />
              ))}
            </span>
            <button type="button" className={styles.wcCancel} onClick={() => stopRec(false)}>
              {t("rcWcCancel")}
            </button>
          </div>
        ) : (
          <div className={styles.wcBar}>
            {show.comment ? (
              <textarea
                ref={textRef}
                rows={1}
                dir="auto"
                maxLength="800"
                placeholder={t("rcWcPh")}
                aria-label={t("rcWcLabel")}
                value={comment}
                onChange={(e) => {
                  setTouched(true);
                  setComment(cleanText(e.target.value));
                }}
              />
            ) : (
              <span className={styles.wcSpacer} />
            )}
            {show.photos && (
              <button
                type="button"
                className={styles.wcIb}
                aria-label={t("rcWcCamera")}
                disabled={photos.length >= MAX_PHOTOS}
                onClick={photoSource.openSource}
              >
                <Camera size={22} strokeWidth={2} aria-hidden />
              </button>
            )}
          </div>
        )}
        {show.voice && (
          <button
            type="button"
            className={classNames(styles.wcMic, { [styles.rec]: rec, [styles.pulse]: !count && !rec && !touched })}
            aria-label={t("rcWcMic")}
            onClick={() => (rec ? stopRec(true) : startRec())}
          >
            {rec ? <Check size={24} strokeWidth={2.8} aria-hidden /> : <Mic size={24} strokeWidth={2.2} aria-hidden />}
          </button>
        )}
      </div>
      {show.comment && !rec && <div className={styles.count}>{comment.length} / 800</div>}
      {show.voice && <div className={styles.wcHint}>{rec ? t("rcWcHintRec") : t("rcWcHint")}</div>}
      {fileError && <div className={classNames(styles.hint, styles.isBad)}>{t(fileError)}</div>}
      {photoSource.element}
    </div>
  );
}

// Label row of the message box: "Racontez-nous…" + required mark + "n ajouté(s)"
export function MessageLabel({ required, count }) {
  const { t } = useTranslation();
  return (
    <div className={classNames(styles.fieldLabel, styles.wcQ)}>
      <span>
        {t("rcCommentLabel")}
        {required && <span className={styles.req}> *</span>}
      </span>
      {count > 0 && <span className={styles.wcCnt}>{t("rcWcAdded", { n: count })}</span>}
    </div>
  );
}

// ── Searchable picker ──────────────────────────────────────────────────────────

// A select whose options are picked in a sheet with a search box, filtered as you type
// (accents ignored). The search box is not focused: the keyboard opens only when tapped.
// `options` are [{ value, label }].
export function SearchSelect({ value, onChange, placeholder, title, options, disabled }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const current = options.find((o) => o.value === value);
  const rows = useMemo(() => {
    const q = normalize(query);
    return q ? options.filter((o) => normalize(o.label).includes(q)) : options;
  }, [options, query]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      <button
        type="button"
        className={classNames(styles.select, styles.searchSelect, { [styles.isPlaceholder]: !current })}
        disabled={disabled}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <span>{current ? current.label : placeholder}</span>
        <ChevronDown className={styles.chevron} size={16} aria-hidden />
      </button>
      {open && (
        <Sheet tall title={title} onClose={close}>
          <div className={styles.plSearch}>
            <Search size={18} aria-hidden />
            <input
              type="search"
              className={styles.input}
              autoComplete="off"
              placeholder={placeholder}
              aria-label={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className={styles.plList} style={{ marginTop: 10 }}>
            {rows.length === 0 && <div className={styles.plEmpty}>{t("noResults")}</div>}
            {rows.map((o) => (
              <button
                type="button"
                key={o.value}
                className={classNames(styles.plRow, { [styles.isCurrent]: o.value === value })}
                onClick={() => {
                  onChange(o.value);
                  close();
                }}
              >
                <span className={styles.plName}>{o.label}</span>
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </>
  );
}

// ── Location ───────────────────────────────────────────────────────────────────

// Searchable wilaya and commune pickers. `value` is { wilayaId, commune }; the wilaya name sent is wilaya.text.
export function LocationFields({ locations, value, onChange, isRequired, children }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const wilayaCommunes = useMemo(() => locations.communes.filter((c) => c.wilaya_id === value.wilayaId), [locations.communes, value.wilayaId]);
  return (
    <>
      <div className={styles.field} data-field="willaya">
        <span className={styles.fieldLabel}>
          {t("rcWilaya")}
          {isRequired.wilaya && <span className={styles.req}> *</span>}
        </span>
        <SearchSelect
          value={value.wilayaId}
          title={t("rcWilaya")}
          placeholder={t("rcWilayaPh")}
          options={locations.wilayas.map((w) => ({ value: w.id, label: `${w.id} · ${isRtl ? w.text_ar : w.text}` }))}
          onChange={(wilayaId) => onChange({ wilayaId, commune: "" })}
        />
      </div>
      {/* the extra field (shop) on the left, commune on the right, in every language (client request) */}
      <div className={children ? styles.twoCol : undefined}>
        {children}
        <div className={styles.field} data-field="commune">
          <span className={styles.fieldLabel}>
            {t("rcCommune")}
            {isRequired.commune && <span className={styles.req}> *</span>}
          </span>
          <SearchSelect
            value={value.commune}
            title={t("rcCommune")}
            placeholder={t("chooseCommune")}
            disabled={!value.wilayaId}
            options={wilayaCommunes.map((c) => ({ value: c.name, label: isRtl ? c.ar_name : c.name }))}
            onChange={(commune) => onChange({ ...value, commune })}
          />
        </div>
      </div>
    </>
  );
}

// "Wilaya · Commune" in the current language, for summary rows
export const locationNames = (locations, value, isRtl) => {
  const wilaya = locations.wilayas.find((w) => w.id === value.wilayaId);
  const commune = locations.communes.find((c) => c.wilaya_id === value.wilayaId && c.name === value.commune);
  return [wilaya && (isRtl ? wilaya.text_ar : wilaya.text), commune && (isRtl ? commune.ar_name : commune.name)].filter(Boolean);
};
